import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Check, Download, Plus, Trash2, Crown, RefreshCw, Printer } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December'];
const TYPE_LABELS = {
  birthday: 'Birthday', anniversary: 'Anniversary', wedding_anniversary: 'Wedding anniversary', other: 'Other',
};
const FORMAT_LABELS = { wall: 'Wall calendar (portrait)', desk: 'Desk calendar (landscape)' };
const PAPER_LABELS = { letter: 'US Letter', a4: 'A4' };

function blankDate() {
  return { label: '', date: '', type: 'birthday', recurring: true, show_years: false };
}

function MonthPreview({ month, headers, photo }) {
  return (
    <div className="aura-card rounded-xl overflow-hidden" role="group" aria-label={`${month.name} ${month.year}`}>
      <div className="aspect-[4/3] bg-black/30">
        {photo ? <img src={`${API_URL}${photo.url}`} alt="" loading="lazy" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center text-xs text-text-muted">No photo</div>}
      </div>
      <div className="p-2">
        <div className="flex justify-between text-xs text-text-light mb-1">
          <span className="font-serif">{month.name}</span><span className="text-text-muted">{month.year}</span>
        </div>
        <table className="w-full text-[9px] text-text-muted" aria-hidden="true">
          <thead><tr>{headers.map((h) => <th key={h} className="font-normal">{h[0]}</th>)}</tr></thead>
          <tbody>
            {month.weeks.map((w, i) => (
              <tr key={i}>{w.map((d, j) => {
                const marked = month.dates.some((x) => x.day === d);
                return <td key={j} className={`text-center ${marked ? 'text-champagne font-bold' : ''}`}>{d || ''}</td>;
              })}</tr>
            ))}
          </tbody>
        </table>
        {month.dates.length > 0 && (
          <ul className="mt-1 text-[10px] text-text-light space-y-0.5">
            {month.dates.map((d, i) => <li key={i}>{d.day} · {d.text}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const { auraId } = useParams();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [dates, setDates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [build, setBuild] = useState(null);
  const [error, setError] = useState('');
  const [dateErrors, setDateErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState('');
  const poll = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/auras/${auraId}/calendar`, { withCredentials: true });
      setData(d); setSettings(d.settings); setDates(d.personal_dates); setPreview(d.preview); setBuild(d.build);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load the calendar.');
    }
  }, [auraId]);

  useEffect(() => { load(); return () => clearInterval(poll.current); }, [load]);

  const startPolling = useCallback(() => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const { data: b } = await axios.get(`${API_URL}/api/auras/${auraId}/calendar/status`, { withCredentials: true });
        setBuild(b);
        if (b.status !== 'queued' && b.status !== 'building') clearInterval(poll.current);
      } catch { clearInterval(poll.current); }
    }, 3000);
  }, [auraId]);

  useEffect(() => {
    if (build && (build.status === 'queued' || build.status === 'building')) startPolling();
  }, [build?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error && !data) {
    return <div className="min-h-screen bg-midnight"><SiteHeader /><div role="alert" className="max-w-3xl mx-auto p-6 text-text-muted">{error}</div></div>;
  }
  if (!data || !settings) {
    return <div className="min-h-screen bg-midnight flex items-center justify-center"><div className="text-champagne animate-pulse font-serif text-xl">Loading…</div></div>;
  }

  const photosById = Object.fromEntries(data.photos.map((p) => [p.id, p]));
  const change = (patch) => { setSettings((s) => ({ ...s, ...patch })); setDirty(true); setStatus(''); };
  const changeDate = (i, patch) => { setDates((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d))); setDirty(true); setStatus(''); };
  const removeDate = (i) => { setDates((ds) => ds.filter((_, j) => j !== i)); setDirty(true); };
  const addDate = (d) => { setDates((ds) => [...ds, d || blankDate()]); setDirty(true); };
  const setMonthPhoto = (idx, id) => {
    const picks = { ...(settings.month_photos || {}) };
    if (id) picks[String(idx)] = Number(id); else delete picks[String(idx)];
    change({ month_photos: picks });
  };

  const validateDates = () => {
    const errs = {};
    dates.forEach((d, i) => {
      if (!d.label.trim()) errs[`${i}-label`] = 'Give this date a name.';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date)) errs[`${i}-date`] = 'Choose a date.';
    });
    setDateErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const save = async () => {
    if (!validateDates()) { setStatus('Please fix the highlighted dates.'); return false; }
    setSaving(true); setError('');
    try {
      const { data: r } = await axios.put(`${API_URL}/api/auras/${auraId}/calendar`,
        { settings, personal_dates: dates }, { withCredentials: true });
      setSettings(r.settings); setDates(r.personal_dates); setPreview(r.preview); setDirty(false);
      setStatus('Saved.');
      return true;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save.');
      return false;
    } finally { setSaving(false); }
  };

  const buildPdf = async () => {
    if (dirty && !(await save())) return;
    try {
      const { data: b } = await axios.post(`${API_URL}/api/auras/${auraId}/calendar/build`, {}, { withCredentials: true });
      setBuild(b); startPolling();
    } catch (e) { setError(e.response?.data?.detail || 'Could not start the build.'); }
  };

  const building = build && (build.status === 'queued' || build.status === 'building');
  const years = [];
  const thisYear = new Date().getFullYear();
  for (let y = thisYear - 1; y <= thisYear + 3; y += 1) years.push(y);

  return (
    <div className="min-h-screen bg-midnight">
      <SiteHeader />
      <div className="border-b border-champagne/10 px-4 py-3 sticky top-0 bg-midnight z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-2 flex-wrap">
          <Link to={`/aura/${auraId}`} className="text-text-muted hover:text-champagne flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to {data.aura.title}
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving || !dirty} className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50" data-testid="calendar-save">
              {!dirty && status === 'Saved.' ? <><Check className="w-3 h-3" /> Saved</> : <><Save className="w-3 h-3" /> {saving ? 'Saving…' : 'Save'}</>}
            </button>
            {data.pdf_allowed && (
              <button onClick={buildPdf} disabled={building} className="btn-champagne text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50" data-testid="calendar-build">
                <RefreshCw className={`w-3 h-3 ${building ? 'animate-spin' : ''}`} /> {building ? 'Building…' : 'Build PDF'}
              </button>
            )}
            {data.print_enabled && (
              <Link to={`/aura/${auraId}/print`} className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1" data-testid="calendar-order-print">
                <Printer className="w-3 h-3" /> Order printed
              </Link>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif text-text-light">Calendar</h1>
          <p className="text-text-muted text-sm mt-1">Twelve months of your event&rsquo;s photos, with the dates that matter to you.</p>
        </div>

        <div aria-live="polite" className="sr-only">{status}</div>
        {error && <div role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3">{error}</div>}

        {!data.pdf_allowed && (
          <div className="aura-card rounded-2xl p-5 border border-champagne/40" data-testid="calendar-upgrade">
            <div className="flex items-center gap-2 mb-1"><Crown className="w-5 h-5 text-champagne" /><h2 className="text-lg font-serif text-text-light">Download as PDF</h2></div>
            <p className="text-text-muted text-sm mb-3">{data.upgrade_message}</p>
            <Link to="/pricing" className="btn-champagne text-sm px-4 py-2 inline-block">View plans</Link>
          </div>
        )}

        {build && build.status !== 'idle' && (
          <div className="aura-card rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3" aria-live="polite" data-testid="calendar-status">
            <div className="text-sm text-text-light">
              {building && 'Building your calendar…'}
              {build.status === 'ready' && `Your calendar is ready${build.built_at ? ` (built ${new Date(build.built_at).toLocaleString()})` : ''}.`}
              {build.status === 'failed' && (build.error || 'The build failed. Please try again.')}
            </div>
            {build.pdf_url && !building && (
              <a href={`${API_URL}${build.pdf_url}`} className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1" data-testid="calendar-download">
                <Download className="w-3 h-3" /> Download PDF
              </a>
            )}
          </div>
        )}

        <section className="aura-card rounded-2xl p-6 space-y-4" aria-labelledby="cal-layout">
          <h2 id="cal-layout" className="text-lg font-serif text-text-light">Layout</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-sm text-text-muted block mb-2">Title</span>
              <input className="aura-input w-full" maxLength={80} placeholder={data.aura.title} value={settings.title || ''}
                onChange={(e) => change({ title: e.target.value || null })} />
            </label>
            <label className="block">
              <span className="text-sm text-text-muted block mb-2">Starts in</span>
              <select className="aura-input w-full" value={settings.start_month} onChange={(e) => change({ start_month: Number(e.target.value) })}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-text-muted block mb-2">Year</span>
              <select className="aura-input w-full" value={settings.start_year} onChange={(e) => change({ start_year: Number(e.target.value) })}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-text-muted block mb-2">Weeks start on</span>
              <select className="aura-input w-full" value={settings.week_start} onChange={(e) => change({ week_start: e.target.value })}>
                <option value="sunday">Sunday</option><option value="monday">Monday</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-text-muted block mb-2">PDF layout</span>
              <select className="aura-input w-full" value={settings.format} onChange={(e) => change({ format: e.target.value })}>
                {data.options.format.map((f) => <option key={f} value={f}>{FORMAT_LABELS[f] || f}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-text-muted block mb-2">PDF paper</span>
              <select className="aura-input w-full" value={settings.home_paper} onChange={(e) => change({ home_paper: e.target.value })}>
                {data.options.home_paper.map((p) => <option key={p} value={p}>{PAPER_LABELS[p] || p}</option>)}
              </select>
            </label>
          </div>
        </section>

        <section className="aura-card rounded-2xl p-6 space-y-4" aria-labelledby="cal-dates">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 id="cal-dates" className="text-lg font-serif text-text-light">Personal dates</h2>
            <button type="button" onClick={() => addDate()} className="btn-outline text-xs px-3 py-1.5 flex items-center gap-1" data-testid="calendar-add-date">
              <Plus className="w-3 h-3" /> Add a date
            </button>
          </div>
          <p className="text-xs text-text-muted">Birthdays and anniversaries are printed in their day and listed under each month. A February 29 date shows on February 28 in other years.</p>
          {data.suggested_date && (
            <div className="rounded-lg border border-champagne/30 px-4 py-3 text-sm text-text-light flex flex-wrap items-center justify-between gap-2">
              <span>Add your event date ({data.suggested_date.date}) as &ldquo;{data.suggested_date.label}&rdquo;?</span>
              <button type="button" className="btn-champagne text-xs px-3 py-1.5" onClick={() => { addDate({ ...data.suggested_date }); setData({ ...data, suggested_date: null }); }}>Add it</button>
            </div>
          )}
          {dates.length === 0 && <p className="text-sm text-text-muted">No dates yet.</p>}
          <ul className="space-y-3">
            {dates.map((d, i) => (
              <li key={i} className="rounded-lg border border-white/10 p-3">
                <fieldset>
                  <legend className="sr-only">Date {i + 1}</legend>
                  <div className="grid sm:grid-cols-4 gap-3">
                    <label className="block">
                      <span className="text-xs text-text-muted block mb-1">Name</span>
                      <input className="aura-input w-full" maxLength={60} value={d.label} placeholder="e.g. Mum's birthday"
                        aria-invalid={!!dateErrors[`${i}-label`]} aria-describedby={dateErrors[`${i}-label`] ? `d${i}-label-err` : undefined}
                        onChange={(e) => changeDate(i, { label: e.target.value })} />
                      {dateErrors[`${i}-label`] && <span id={`d${i}-label-err`} role="alert" className="text-xs text-red-300">{dateErrors[`${i}-label`]}</span>}
                    </label>
                    <label className="block">
                      <span className="text-xs text-text-muted block mb-1">Date</span>
                      <input type="date" className="aura-input w-full" value={d.date}
                        aria-invalid={!!dateErrors[`${i}-date`]} aria-describedby={dateErrors[`${i}-date`] ? `d${i}-date-err` : undefined}
                        onChange={(e) => changeDate(i, { date: e.target.value })} />
                      {dateErrors[`${i}-date`] && <span id={`d${i}-date-err`} role="alert" className="text-xs text-red-300">{dateErrors[`${i}-date`]}</span>}
                    </label>
                    <label className="block">
                      <span className="text-xs text-text-muted block mb-1">Kind</span>
                      <select className="aura-input w-full" value={d.type} onChange={(e) => changeDate(i, { type: e.target.value })}>
                        {data.options.date_types.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
                      </select>
                    </label>
                    <div className="flex items-end justify-end">
                      <button type="button" onClick={() => removeDate(i)} className="btn-outline text-xs px-3 py-2 flex items-center gap-1" aria-label={`Remove ${d.label || 'this date'}`}>
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-text-light">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={d.recurring} onChange={(e) => changeDate(i, { recurring: e.target.checked })} /> Every year</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={d.show_years} disabled={!d.recurring} onChange={(e) => changeDate(i, { show_years: e.target.checked })} /> Show the years (e.g. &ldquo;10th anniversary&rdquo;)</label>
                  </div>
                </fieldset>
              </li>
            ))}
          </ul>
        </section>

        <section className="aura-card rounded-2xl p-6" aria-labelledby="cal-preview">
          <h2 id="cal-preview" className="text-lg font-serif text-text-light mb-1">Months</h2>
          <p className="text-xs text-text-muted mb-4">Photos are picked automatically (the most loved, spread across your event, never the same one twice). Choose your own for any month; save to refresh the preview.</p>
          {preview && (
            <ol className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="calendar-preview">
              {preview.months.map((m) => (
                <li key={m.index}>
                  <MonthPreview month={m} headers={preview.day_headers} photo={photosById[m.photo_id]} />
                  <label className="block mt-1">
                    <span className="sr-only">Photo for {m.name} {m.year}</span>
                    <select className="aura-input w-full text-xs" value={settings.month_photos?.[String(m.index)] || ''}
                      onChange={(e) => setMonthPhoto(m.index, e.target.value)}>
                      <option value="">Auto-pick</option>
                      {data.photos.map((p, k) => <option key={p.id} value={p.id}>Photo {k + 1}{p.guest_name ? ` (${p.guest_name})` : ''}</option>)}
                    </select>
                  </label>
                </li>
              ))}
            </ol>
          )}
        </section>
      </main>
    </div>
  );
}
